<template>
  <div class="blog-page">
    <!-- SEO Meta Tags -->
    <meta name="description" content="Modern Kurs blog - Eğitim teknolojileri, randevu sistemleri ve eğitim yönetimi hakkında güncel yazılar ve makaleler." />
    <meta name="keywords" content="Modern Kurs blog, eğitim teknolojileri, randevu sistemi, eğitim yönetimi, blog yazıları" />
    <meta name="author" content="Modern Kurs" />
    <meta property="og:title" content="Blog - Modern Kurs" />
    <meta property="og:description" content="Eğitim teknolojileri ve eğitim yönetimi hakkında güncel yazılar." />
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://modernkurs.com/blogs" />

    <!-- Navigation Bar -->
    <CorporateNavbar />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">Blog & Makaleler</h1>
          <p class="hero-subtitle">Eğitim hakkında güncel yazılar, ipuçları ve rehberler</p>
        </div>
      </div>
    </section>

    <!-- Blog Content -->
    <section class="blog-content">
      <div class="container">
        <!-- Blog Grid -->
        <div class="blog-grid" v-if="blogs.length > 0">
          <article v-for="blog in blogs" :key="blog.id" class="blog-card" @click="viewBlog(blog)">
            <div class="blog-image">
              <img v-if="blog.image_url" :src="resolveImageUrl(blog.image_url)" :alt="blog.title" />
              <div v-else class="blog-placeholder">
                <span>📝</span>
              </div>
            </div>
            <div class="blog-content">
              <div class="blog-meta">
                <span class="blog-date">{{ formatDate(blog.created_at) }}</span>
                <span class="blog-category">{{ blog.category || 'Genel' }}</span>
              </div>
              <h3 class="blog-title">{{ blog.title }}</h3>
              <p class="blog-excerpt">{{ truncateContent(blog.content, 150) }}</p>
              <div class="blog-footer">
                <router-link :to="`/blog/${blog.id}`" class="read-more">
                  Devamını Oku →
                </router-link>
              </div>
            </div>
          </article>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading" class="empty-state">
          <div class="empty-icon">📝</div>
          <h3>Henüz Blog Yazısı Yok</h3>
          <p>Yakında eğitim teknolojileri hakkında değerli içerikler paylaşacağız.</p>
          <router-link to="/" class="btn btn-primary">Ana Sayfaya Dön</router-link>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Blog yazıları yükleniyor...</p>
        </div>
      </div>
    </section>
    <!-- WhatsApp Floating Button -->
    <WhatsAppButton />

    <!-- Footer -->
    <CorporateFooter />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import CorporateNavbar from '@/components/CorporateNavbar.vue'
import CorporateFooter from '@/components/CorporateFooter.vue'
import WhatsAppButton from '@/components/WhatsAppButton.vue'

export default {
  name: 'Blog',
  components: {
    CorporateNavbar,
    CorporateFooter,
    WhatsAppButton
  },
  setup() {
    // API'den yüklenen bloglar (sadece 'published' olanlar)
    const blogs = ref([])
    const loading = ref(true)

    const loadBlogs = async () => {
      try {
        loading.value = true
        const res = await axios.get('/api/blogs')
        blogs.value = res.data || []
      } catch (err) {
        console.error('Bloglar yüklenemedi:', err)
        blogs.value = []
      } finally {
        loading.value = false
      }
    }

    // Görsel URL çözümleme — /uploads/... backend'den geliyor, http(s) URL'ler doğrudan
    const resolveImageUrl = (url) => {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) return url
      if (url.startsWith('/uploads/')) return axios.defaults.baseURL + url
      return url
    }

    const viewBlog = (blog) => {
      // Blog detay sayfasına yönlendir (router-link kullanılıyor, bu callback log için)
      void blog
    }

    const truncateContent = (content, maxLength) => {
      if (!content) return ''
      return content.length > maxLength
        ? content.substring(0, maxLength) + '...'
        : content
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    onMounted(loadBlogs)

    return {
      blogs,
      loading,
      viewBlog,
      truncateContent,
      formatDate,
      resolveImageUrl
    }
  }
}
</script>

<style scoped>
.blog-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  margin-top: 80px; /* Navbar yüksekliği kadar margin */
  overflow-x: hidden;
  width: 100%;
}



/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  color: white;
  padding: 3rem 0;
  text-align: center;
  margin-top: 55px;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
}

.hero-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  line-height: 1.6;
}

/* Blog Content */
.blog-content {
  padding: 4rem 0;
  flex: 1;
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.blog-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.blog-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.blog-image {
  height: 200px;
  overflow: hidden;
}

.blog-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.blog-card:hover .blog-image img {
  transform: scale(1.05);
}

.blog-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-size: 3rem;
}

.blog-content {
  padding: 1.5rem;
}

.blog-meta {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #666;
}

.blog-category {
  background: #667eea;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
}

.blog-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
  line-height: 1.4;
}

.blog-excerpt {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.blog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.read-more {
  color: #667eea;
  font-weight: 500;
  transition: color 0.3s ease;
}

.blog-card:hover .read-more {
  color: #5a6fd8;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.empty-state p {
  color: #666;
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}



/* Container */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

/* Responsive */
@media (max-width: 1200px) {
  .container {
    max-width: 1000px;
    padding: 0 2rem;
  }

  .blog-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .container {
    max-width: 100%;
    padding: 0 1.5rem;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .blog-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .blog-card {
    margin-bottom: 1rem;
  }

  .blog-content {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .container {
    padding: 0 1rem;
  }
}
</style>
